import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const slideUp = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const SplashContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, var(--gradient-start), var(--gradient-end))',
  zIndex: 9999,
  animation: `${fadeIn} 0.5s ease-in-out forwards`,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'52\' height=\'26\' viewBox=\'0 0 52 26\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ff69b4\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z\' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
    opacity: 0.7,
    zIndex: 0,
  }
}));

// Custom SVG Heart
const HeartSVG = () => (
  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
      fill="url(#gradient)" 
    />
    <defs>
      <linearGradient id="gradient" x1="2" y1="3" x2="22" y2="21" gradientUnits="userSpaceOnUse">
        <stop stopColor="var(--gradient-start)" />
        <stop offset="1" stopColor="var(--gradient-end)" />
      </linearGradient>
    </defs>
  </svg>
);

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  zIndex: 2,
}));

const AnimatedHeart = styled(Box)(({ theme }) => ({
  animation: `${pulseAnimation} 1.5s infinite ease-in-out`,
  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))',
}));

const AppTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  marginBottom: theme.spacing(1),
  background: 'linear-gradient(45deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${slideUp} 0.6s ease-out forwards`,
  animationDelay: '0.2s',
  opacity: 0,
}));

const Tagline = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  animation: `${slideUp} 0.6s ease-out forwards`,
  animationDelay: '0.4s',
  opacity: 0,
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  animation: `${fadeIn} 0.6s ease-out forwards`,
  animationDelay: '0.6s',
  opacity: 0,
}));

const ProgressIndicator = styled(CircularProgress)(({ theme }) => ({
  color: 'var(--gradient-start)',
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '0.7rem',
  fontWeight: 'bold',
}));

const SplashScreen = ({ onFinished }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prevProgress + (100 / 30); // Increment to reach 100 in 3 seconds (30 increments * 100ms)
      });
    }, 100);

    const redirect = setTimeout(() => {
      if (onFinished) onFinished();
    }, 3000);

    return () => {
      clearInterval(timer);
      clearTimeout(redirect);
    };
  }, [onFinished]);

  return (
    <SplashContainer>
      <LogoContainer>
        <AnimatedHeart>
          <HeartSVG />
        </AnimatedHeart>
        <AppTitle variant="h1">Amour Space</AppTitle>
        <Tagline variant="body1">Where Hearts Connect</Tagline>
      </LogoContainer>
      <ProgressContainer>
        <ProgressIndicator variant="determinate" value={progress} size={50} thickness={4} />
        <ProgressText variant="caption">{Math.round(progress)}%</ProgressText>
      </ProgressContainer>
    </SplashContainer>
  );
};

export default SplashScreen; 